/**
 * Tabbed panels.
 *
 * The markup carries every panel at once, so a visitor without JavaScript
 * reads all of them one under another. This module turns the group into real
 * tabs: one panel at a time, the rest hidden, the whole thing driven by the
 * keyboard as well.
 *
 * ```html
 * <div class="tabs" data-tabs>
 *     <div class="tabs__list" role="tablist">
 *         <button class="tabs__tab" data-tab="message">Správa</button>
 *     </div>
 *     <div class="tabs__panel" data-tab-panel="message">…</div>
 * </div>
 * ```
 */

import { queryAll } from "../core/dom";

const ACTIVE_CLASS = "is-active";
const READY_CLASS = "is-ready";

/** Show the panel of `name` inside `group` and hide the others. */
export function selectTab(group: HTMLElement, name: string): void {
    for (const tab of queryAll<HTMLElement>("[data-tab]", group)) {
        const selected = tab.dataset["tab"] === name;
        tab.classList.toggle(ACTIVE_CLASS, selected);
        tab.setAttribute("aria-selected", selected ? "true" : "false");
        tab.tabIndex = selected ? 0 : -1;
    }

    for (const panel of queryAll<HTMLElement>("[data-tab-panel]", group)) {
        const selected = panel.dataset["tabPanel"] === name;
        panel.classList.toggle(ACTIVE_CLASS, selected);
        panel.hidden = !selected;
    }
}

/** Return the name the group should open on. */
function initialTab(group: HTMLElement, tabs: readonly HTMLElement[]): string {
    const wanted = group.dataset["tabsActive"];
    if (wanted && tabs.some((tab) => tab.dataset["tab"] === wanted)) {
        return wanted;
    }

    return tabs[0]?.dataset["tab"] ?? "";
}

/** Move the focus to the neighbouring tab, wrapping around at both ends. */
function focusNeighbour(tabs: readonly HTMLElement[], current: HTMLElement, step: number): void {
    const index = tabs.indexOf(current);
    if (index === -1) {
        return;
    }

    const next = tabs[(index + step + tabs.length) % tabs.length];
    next?.focus();
    next?.click();
}

/** Turn a single group into tabs. */
export function initTabGroup(group: HTMLElement): void {
    const tabs = queryAll<HTMLElement>("[data-tab]", group);

    // A lone panel is left as it is, with its own heading and nothing to switch.
    if (tabs.length < 2) {
        return;
    }

    const list = group.querySelector<HTMLElement>(".tabs__list");
    list?.setAttribute("role", "tablist");

    const panels = queryAll<HTMLElement>("[data-tab-panel]", group);

    for (const tab of tabs) {
        const name = tab.dataset["tab"] ?? "";
        const panel = panels.find((item) => item.dataset["tabPanel"] === name);

        tab.setAttribute("role", "tab");
        tab.setAttribute("aria-controls", panel?.id ?? "");
        panel?.setAttribute("role", "tabpanel");
        panel?.setAttribute("aria-label", tab.textContent?.trim() ?? "");

        tab.addEventListener("click", (event: Event) => {
            event.preventDefault();
            selectTab(group, name);
        });

        tab.addEventListener("keydown", (event: KeyboardEvent) => {
            if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
                event.preventDefault();
                focusNeighbour(tabs, tab, event.key === "ArrowRight" ? 1 : -1);
            }
        });
    }

    group.classList.add(READY_CLASS);
    selectTab(group, initialTab(group, tabs));
}

/** Wire every tab group of the current page. */
export function initTabs(): void {
    for (const group of queryAll<HTMLElement>("[data-tabs]")) {
        initTabGroup(group);
    }
}
